import { EditStrategyPageInvestmentQuery, Security_Status } from '@/__generated__/graphql';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input, { ErrorText } from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import Label from '@/components/basicComponents/label';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { UPDATE_INVESTMENT_MUTATION } from '@/components/dueDiligence-page/queries';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '@/components/investors-page/queries';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FormFieldsWrapper } from '..';
import { UPDATE_INVESTMENT_RESTRICTIONS_MUTATION } from '../../queries';
import { Tenant } from '../../types';
import { DEFAULT_RESTRICTIONS_ERROR, defaultListData, distributionSettingsOptions, yseNoOptions } from '../constants';
import Dialog, { DialogStatus } from '../modals/dialog';
import { RadioWithDynamicSelects } from '../radioWithDynamicSelects';
import { IListData } from '../types';
import { extractDirtyFields, getInvestmentRestrictionsData, isFieldRequiredByStatus } from '../utils';

interface IForm {
  minimum: string;
  type: string;
  ubtiBlocker: boolean;
  custodied: boolean;
}

const defaultFormValues: IForm = {
  minimum: '',
  type: '',
  ubtiBlocker: null,
  custodied: null
};

interface AllocationSettingsModalPageProps {
  title: string;
  investmentId?: string;
  formData?: IForm | {};
  investment?: {
    securityStatus: Security_Status;
    advisoryFirms: EditStrategyPageInvestmentQuery['Investment']['advisoryFirms'];
    showToAllAdvisoryFirms: boolean;
  };
  investmentRefetch: () => void;
  handleCloseEditModalPage: () => void;
}

export const AllocationSettingsModalPage = ({
  title,
  investmentId,
  formData = {},
  investment,
  investmentRefetch,
  handleCloseEditModalPage
}: AllocationSettingsModalPageProps) => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const {
    handleSubmit,
    getValues,
    control,
    reset,
    formState: { dirtyFields }
  } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogStatus, setDialogStatus] = useState<DialogStatus>();
  const [selectedDistributionSettingsOptions, setSelectedDistributionSettingsOptions] = useState(distributionSettingsOptions[0]);
  const [updateInvestmentRestrictionsErrors, setUpdateInvestmentRestrictionsErrors] = useState<string[] | null>(null);
  const [advisoryFirms, setAdvisoryFirms] = useState<IListData>(defaultListData);

  const [updateInvestment] = useMutation(UPDATE_INVESTMENT_MUTATION);
  const [updateInvestmentRestrictions] = useMutation(UPDATE_INVESTMENT_RESTRICTIONS_MUTATION);

  const { data: advisoryFirmListData, loading: advisoryFirmListLoading } = useQuery<{ allAdvisoryFirmList: Tenant[] }>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const advisoryFirmList = data.allAdvisoryFirmList.map((advisoryFirm) => advisoryFirm.name);
      setAdvisoryFirms({ ...advisoryFirms, list: advisoryFirmList });
    }
  });

  useEffect(() => {
    if (!investmentId) return;

    reset((defaultFormData) => ({ ...defaultFormData, ...formData }));
    setAdvisoryFirms({
      ...advisoryFirms,
      chosen: !investment?.showToAllAdvisoryFirms ? investment?.advisoryFirms?.map((advisoryFirm) => advisoryFirm.name) ?? [] : []
    });
    setSelectedDistributionSettingsOptions(investment?.showToAllAdvisoryFirms ? distributionSettingsOptions[0] : distributionSettingsOptions[1]);
  }, [formData, investment]);

  useEffect(() => {
    setAdvisoryFirms((prevAdvisoryFirms) => {
      return {
        ...prevAdvisoryFirms,
        list: prevAdvisoryFirms.list
          .filter((advisoryFirm) => !advisoryFirms.chosen.includes(advisoryFirm))
          .sort((a: string, b: string) => a.localeCompare(b))
      };
    });
  }, [advisoryFirmListData, advisoryFirms.chosen]);

  const onCloseDialog = () => setDialogOpen(false);
  const onDialogSuccess = () => {
    reset();
    onCloseDialog();
    handleCloseEditModalPage();
  };

  const onUpdateInvestment = async (data: IForm) => {
    if (!investmentId) return;
    if (
      selectedDistributionSettingsOptions === distributionSettingsOptions[1] &&
      (!advisoryFirms.chosen.length || advisoryFirms.chosen.includes('None'))
    ) {
      setUpdateInvestmentRestrictionsErrors([DEFAULT_RESTRICTIONS_ERROR]);
      return;
    }
    try {
      setUpdateInvestmentRestrictionsErrors(null);
      setDialogStatus(DialogStatus.LOADING);
      setDialogOpen(true);
      const dirtyData = extractDirtyFields(data, dirtyFields);

      await updateInvestment({
        variables: {
          data: {
            id: investmentId,
            ...dirtyData,
            ...(dirtyFields?.minimum && { minimum: parseFloat(dirtyData.minimum) })
          }
        }
      });

      await updateInvestmentRestrictions({
        variables: {
          data: getInvestmentRestrictionsData(
            investmentId,
            selectedDistributionSettingsOptions,
            advisoryFirmListData?.allAdvisoryFirmList ?? [],
            advisoryFirms
          )
        }
      });

      investmentRefetch();
      setDialogStatus(DialogStatus.SUCCESS);
      setDialogOpen(true);
    } catch (error: any) {
      const message = (error?.graphQLErrors[0]?.extensions?.originalError as { message?: string[] })?.message ?? null;
      if (message) {
        setUpdateInvestmentRestrictionsErrors(message);
      }
      setDialogStatus(DialogStatus.FAILED);
      setDialogOpen(true);
    }
  };

  if (advisoryFirmListLoading) return <LoaderOnWholeScreen />;

  return (
    <MainWrap>
      <Dialog
        isOpen={isDialogOpen}
        onClose={onCloseDialog}
        onSuccess={onDialogSuccess}
        reFetch={() => onUpdateInvestment(getValues())}
        status={dialogStatus}
        updateInvestmentRestrictionsErrors={updateInvestmentRestrictionsErrors}
      />
      <Header modalControl={<GoBackButton handleClose={handleCloseEditModalPage} backToTitle={''} />} />
      <PageTitle title={title ?? ''} />
      <PaddingWrap>
        <Form id="allocationSettingsForm" onSubmit={handleSubmit(onUpdateInvestment, () => window.scrollTo(0, 0))}>
          <FormFieldsWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="minimum"
                control={control}
                rules={{
                  required: {
                    value: isFieldRequiredByStatus('minimum', investment?.securityStatus),
                    message: 'Minimum is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    value={value ? formatMaskedDollarInputValue(value) : ''}
                    onChange={(e) => {
                      const formattedValue = formatDollarInputValue(e.target.value);
                      onChange(formattedValue);
                    }}
                    label="Minimum Investor Commitment"
                    errorText={error?.message}
                    withDollarSign
                    size="md"
                    isRequiredField={isFieldRequiredByStatus('minimum', investment?.securityStatus)}
                    tooltipWidth={300}
                    tooltip="Provide the investment minimum. Entities with insufficient capacity will be flagged based on this value."
                  />
                )}
              />
              <Controller
                name="type"
                control={control}
                rules={{
                  required: {
                    value: isFieldRequiredByStatus('type', investment?.securityStatus),
                    message: 'Type is a required field'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    setValue={onChange}
                    options={[createSimpleSelectOption('Fund'), createSimpleSelectOption('Direct')]}
                    errorText={error?.message}
                    size="md"
                    label="Type"
                    isRequiredField={isFieldRequiredByStatus('type', investment?.securityStatus)}
                  />
                )}
              />
            </TwoColumnWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <div>
                <Label
                  size="md"
                  tooltip="Answer Yes if this strategy is suitable for tax exempt investors. Either there is a UBTI Blocker in place, such as an offshore vehicle, or there is no risk of generating UBTI"
                  tooltipWidth={300}
                  required={isFieldRequiredByStatus('ubtiBlocker', location?.state?.status || investment?.securityStatus)}
                >
                  Tax Exempt Suitable
                </Label>
                <Controller
                  name="ubtiBlocker"
                  control={control}
                  rules={{
                    validate: {
                      isBoolean: (value) => {
                        if (isFieldRequiredByStatus('ubtiBlocker', location?.state?.status || investment?.securityStatus)) {
                          return typeof value === 'boolean' || 'Tax Exempt Suitable is required';
                        }
                        return true;
                      }
                    }
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <>
                      <TogglesWrap isMobile={isMobile}>
                        {yseNoOptions.map((option, index) => (
                          <RadioButton
                            key={option + index}
                            name="ubtiBlocker"
                            radioButtonText={option}
                            selectedOption={value === null ? '' : value ? yseNoOptions[0] : yseNoOptions[1]}
                            handleRadioChange={(event) => onChange(event.target.value === yseNoOptions[0])}
                          />
                        ))}
                      </TogglesWrap>
                      {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                    </>
                  )}
                />
              </div>
            </TwoColumnWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <div>
                <Label
                  size="md"
                  tooltipWidth={300}
                  tooltip="Some investor types (e.g., IRAs and DAFs) require that a fund is registered with a custodian in a way that permits these investor types to invest. Use this flag to indicate that this requirement is satisfied for the relevant investors (or not)."
                  required={isFieldRequiredByStatus('custodied', location?.state?.status || investment?.securityStatus)}
                >
                  Custodied
                </Label>
                <Controller
                  name="custodied"
                  control={control}
                  rules={{
                    validate: {
                      isBoolean: (value) => {
                        if (isFieldRequiredByStatus('custodied', location?.state?.status || investment?.securityStatus)) {
                          return typeof value === 'boolean' || 'Custodied is required';
                        }
                        return true;
                      }
                    }
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <>
                      <TogglesWrap isMobile={isMobile}>
                        {yseNoOptions.map((option, index) => (
                          <RadioButton
                            key={option + index}
                            name="custodied"
                            radioButtonText={option}
                            selectedOption={value === null ? '' : value ? yseNoOptions[0] : yseNoOptions[1]}
                            handleRadioChange={(event) => onChange(event.target.value === yseNoOptions[0])}
                          />
                        ))}
                      </TogglesWrap>
                      {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                    </>
                  )}
                />
              </div>
            </TwoColumnWrapper>
            <RadioWithDynamicSelects
              radioOptions={distributionSettingsOptions}
              selectedRadioOption={selectedDistributionSettingsOptions}
              radioLabel="Restrictions"
              radioError={
                updateInvestmentRestrictionsErrors?.[0] && updateInvestmentRestrictionsErrors?.[0]?.includes(DEFAULT_RESTRICTIONS_ERROR)
                  ? updateInvestmentRestrictionsErrors[0]
                  : ''
              }
              dynamicItems={advisoryFirms}
              setDynamicItems={setAdvisoryFirms}
              handleRadioChange={setSelectedDistributionSettingsOptions}
            />
          </FormFieldsWrapper>
          <FormFooter onCancel={handleCloseEditModalPage} disableSaveButton={false} />
        </Form>
      </PaddingWrap>
    </MainWrap>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
`;

const TogglesWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '0')};
`;
